import { Mail } from "lucide-react";
import { Link } from "react-router-dom";
import LegalLayout, { LegalSection } from "@/components/legal/LegalLayout";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { LANDING } from "@/constants/landingTestIds";

const LAST_UPDATED = "February 1, 2026";

const PrivacyPolicyPage = () => {
  useDocumentMeta(
    "Privacy Policy | Tracre One",
    "Read the Tracre One Privacy Policy to understand what data we collect, how it is used, our AI processing practices, and your rights as a user of our real estate CRM app."
  );

  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated={LAST_UPDATED}
      testId={LANDING.privacyPolicyPage}
    >
      <p className="text-slate-600 leading-relaxed">
        Tracre One ("we", "our", "us") is committed to protecting the privacy of every real
        estate agent, broker and property consultant who uses our Android CRM application
        ("App", "Service"). This Privacy Policy explains what information we collect, how we
        use and protect it, and the choices you have. By downloading or using Tracre One, you
        agree to the practices described in this policy.
      </p>

      <LegalSection title="1. Information We Collect">
        <p>We collect the following categories of information to provide and improve our Service:</p>

        <h3 className="font-semibold text-[#0F172A] mt-4 mb-1">Personal Information</h3>
        <p>
          When you register or use Tracre One, we may collect your name, email address, phone
          number, business/agency name, and any lead or client details (name, phone number,
          property preferences, notes) that you manually enter into your CRM pipeline.
        </p>

        <h3 className="font-semibold text-[#0F172A] mt-4 mb-1">Device Information</h3>
        <p>
          We may collect device-related information such as device model, operating system
          version, unique device identifiers, app version, language settings and crash/diagnostic
          logs to help us maintain app stability and compatibility.
        </p>

        <h3 className="font-semibold text-[#0F172A] mt-4 mb-1">Location Data (if applicable)</h3>
        <p>
          If you enable location-based features (e.g. logging a property site visit or tagging
          a lead's location), Tracre One may access your device's approximate or precise
          location with your explicit permission. You can disable this at any time via your
          device settings.
        </p>

        <h3 className="font-semibold text-[#0F172A] mt-4 mb-1">
          Camera, Storage & File Permissions (if applicable)
        </h3>
        <p>
          Tracre One may request camera and storage access so you can attach property photos,
          documents, or brochures to a lead or listing. Files you capture or upload are stored
          locally on your device and, where relevant, within your account for the purpose of
          your CRM workflow only.
        </p>
      </LegalSection>

      <LegalSection title="2. How We Use Data">
        <ul>
          <li>To provide, operate, and maintain core CRM features (pipeline, leads, bookings).</li>
          <li>To personalize your dashboard, reminders, and landing page builder content.</li>
          <li>To send important service notifications, reminders and follow-up alerts.</li>
          <li>To troubleshoot issues, improve performance, and prevent misuse of the App.</li>
          <li>To provide customer support when you contact our team.</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. AI Processing">
        <p>
          Tracre One uses artificial intelligence to power features such as WhatsApp
          automation, lead nurturing suggestions, and instant landing page generation. When you
          use these AI-powered features, relevant text (such as a lead's message or a prompt you
          provide) is securely transmitted over encrypted (HTTPS) connections to our AI
          processing partners solely to generate the requested output.
        </p>
        <p>
          We do not permit this data to be used to train third-party AI models, and it is not
          retained by our AI processing partners beyond what is necessary to fulfil your request.
        </p>
      </LegalSection>

      <LegalSection title="4. Data Sharing">
        <p>
          We do not sell your personal information or your leads' data. We may share limited
          information only in the following circumstances:
        </p>
        <ul>
          <li>With trusted service providers (e.g. cloud hosting, AI processing, messaging APIs) strictly to operate the Service.</li>
          <li>When required by law, regulation, legal process, or government request.</li>
          <li>To protect the rights, property, or safety of Tracre One, our users, or the public.</li>
          <li>With your explicit consent for any other purpose.</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Third-Party Services">
        <p>
          Tracre One integrates with third-party services such as WhatsApp Business messaging
          APIs, cloud AI providers, and analytics tools to deliver core functionality. These
          third parties process data under their own privacy policies and only to the extent
          necessary to provide the relevant feature. We carefully select partners who maintain
          industry-standard data protection practices.
        </p>
      </LegalSection>

      <LegalSection title="6. Cookies & Analytics">
        <p>
          Our website and app may use cookies, local storage, and analytics tools (such as
          usage/event analytics) to understand how our Service is used, measure performance, and
          improve the user experience. You can control cookie preferences through your browser
          settings.
        </p>
      </LegalSection>

      <LegalSection title="7. Data Security">
        <p>
          We implement industry-standard technical and organizational safeguards — including
          encryption in transit (HTTPS/TLS) and secure local storage — to protect your data from
          unauthorized access, alteration, disclosure, or destruction. However, no method of
          electronic transmission or storage is 100% secure, and we cannot guarantee absolute
          security.
        </p>
      </LegalSection>

      <LegalSection title="8. Data Retention">
        <p>
          We retain your personal information and CRM data for as long as your account is
          active or as needed to provide the Service. If you delete your account or uninstall
          the App, locally stored data is removed from your device, and any associated data held
          with us is deleted or anonymized within a reasonable period, unless we are required to
          retain it to comply with legal obligations.
        </p>
      </LegalSection>

      <LegalSection title="9. User Rights">
        <p>You have the right to:</p>
        <ul>
          <li>Access, review, and correct the personal information you have provided.</li>
          <li>
            Request deletion of your account and associated data — see our{" "}
            <Link to="/data-deletion">Data Deletion Request</Link> page for full details on
            the process, timelines, and what data is retained.
          </li>
          <li>Withdraw consent for optional features such as location tracking, at any time.</li>
          <li>Object to certain processing or request a copy of your data.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:support@tracreoneai.com">support@tracreoneai.com</a>.
        </p>
      </LegalSection>

      <LegalSection title="10. Children's Privacy">
        <p>
          Tracre One is a professional B2B tool intended for use by real estate agents, brokers
          and property consultants who are 18 years of age or older. We do not knowingly collect
          personal information from children under 18. If we become aware that we have
          inadvertently collected such information, we will take steps to delete it promptly.
        </p>
      </LegalSection>

      <LegalSection title="11. Changes to This Privacy Policy">
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our
          practices, technology, or legal requirements. Material changes will be communicated
          via in-app notice or email, and the "Last updated" date at the top of this page will
          be revised accordingly. Continued use of the App after changes take effect constitutes
          acceptance of the revised policy.
        </p>
      </LegalSection>

      <LegalSection title="12. Contact Us">
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or your
          data, please reach out to us:
        </p>
        <a
          href="mailto:support@tracreoneai.com"
          className="inline-flex items-center gap-2 mt-2 rounded-lg bg-[#2563EB]/10 text-[#2563EB] font-semibold px-4 py-2.5 hover:bg-[#2563EB]/15 transition-colors"
        >
          <Mail className="w-4 h-4" /> support@tracreoneai.com
        </a>
      </LegalSection>
    </LegalLayout>
  );
};

export default PrivacyPolicyPage;
